import { ErrorMessage } from '@hookform/error-message'
import { Button, Checkbox, Col, Flex, Form, Row, Tooltip, Typography } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { Control, Controller, UseFormClearErrors, UseFormResetField, UseFormSetValue, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CustomSelect from 'src/app/shared/components/common/select'
import { UserRoleIds } from 'src/app/shared/constants/app.constants'
import { STATUS_CODES } from 'src/app/shared/constants/common.constants'
import { IGetClientListName } from 'src/app/shared/models/clientSetup.model'
import { getClientListName } from 'src/app/shared/services/ClientSetupService'
import { colorBorderDisabled } from 'src/app/themes/primaryThemeDefault'
import { BankUserSetupFormValues } from './type'

interface SelectClientProps {
  control: Control<BankUserSetupFormValues>
  errors: any
  clearErrors: UseFormClearErrors<BankUserSetupFormValues>
  resetField: UseFormResetField<BankUserSetupFormValues>
  setValue: UseFormSetValue<BankUserSetupFormValues>
  includesArchived: boolean
}

function SelectClient(props: Readonly<SelectClientProps>) {
  const { t } = useTranslation()
  const { control, errors, clearErrors, setValue } = props
  const [loading, setLoading] = useState<boolean>(false)
  const [clientList, setClientList] = useState<IGetClientListName.Response>([])

  const userRoles = useWatch({
    control,
    name: 'role',
  })

  const [isDisable, setIsDisable] = useState<boolean>(false);

  const handleGetClientListName = useCallback(async () => {
    try {
      setLoading(true)
      const listResponse = await getClientListName(undefined, props.includesArchived)
      if (listResponse.statusCode === STATUS_CODES.SUCCESS) {
        console.log('CLIENT LIST: ', listResponse)
        setClientList(listResponse.data)
      }
    } catch (error) {
      console.log('ERROR: ', error)
    } finally {
      setLoading(false)
    }
  }, [props.includesArchived])

  const handleAssignAllExisting = useCallback(() => {
    clearErrors('clients')
    setValue('clients', clientList.map((item) => item.id) as any, {
      shouldDirty: true,
      shouldTouch: true,
    })
  }, [clearErrors, clientList, setValue])

  const handleUnassignAllExisting = useCallback(() => {
    clearErrors('clients')
    setValue('clients', [] as any, {
      shouldDirty: true,
      shouldTouch: true,
    })
  }, [clearErrors, setValue])

  // get list of client when going to this page
  useEffect(() => {
    handleGetClientListName()
  }, [handleGetClientListName])

  useEffect(() => {
    const disableClientControl: boolean = userRoles?.toLowerCase()?.includes(UserRoleIds.SUPER_ADMIN.toLowerCase());
    if (disableClientControl) {
      setValue('clients', []);
      setValue('isAssignedAllNewClient', false);
      setIsDisable(true);
    } else {
      setIsDisable(false);
    }
  }, [userRoles])

  // get client detail after selecting client name

  return (
    <Flex vertical style={{ paddingBottom: '24px' }}>
      <Typography.Text style={{ fontSize: '18px', fontWeight: '700', lineHeight: '20px', paddingBottom: '4px' }}>
        {t('BankSetup:addNewBankUser.label.selectClient')}
      </Typography.Text>
      <Flex
        vertical
        style={{ border: `1px solid ${colorBorderDisabled}`, padding: '16px 16px 0 16px', borderRadius: '16px' }}
      >
        <Row gutter={24}>
          <Col span={24}>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item label={t('BankSetup:addNewBankUser.label.clientName')}>
                  <Controller
                    name='clients'
                    control={control}
                    render={({ field, fieldState }) => (
                      <Flex align='center' gap={16}>
                        <CustomSelect
                          mode='multiple'
                          status={fieldState.error ? 'error' : ''}
                          value={!field.value ? [] : field.value}
                          onChange={(value) => {
                            console.log('CHANE: ', value)
                            field.onChange(value)
                          }}
                          placeholder={t('BankSetup:addNewBankUser.placeholder.selectClientName')}
                          onBlur={async (event) => {
                            field.onBlur()
                          }}
                          options={clientList.map((item) => ({
                            label: item.clientName,
                            value: item.id,
                          }))}
                          filterOption={(inputValue, option: any) => {
                            return option?.label?.toLowerCase().includes(inputValue.toLowerCase())
                          }}
                          maxTagCount={'responsive'}
                          maxTagPlaceholder={(omittedValues) => (
                            <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                              <span>+ {omittedValues.length}...</span>
                            </Tooltip>
                          )}
                          loading={loading}
                          disabled={isDisable}
                        />
                        <Button
                          type='primary'
                          style={{ height: 42, borderRadius: 12 }}
                          disabled={loading || isDisable}
                          onClick={handleAssignAllExisting}
                        >
                          {t('ClientUserSetup:assignAllExisting')}
                        </Button>
                      </Flex>
                    )}
                  />
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='clients' />
                  </Typography>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={24} xl={12}>
                <Form.Item valuePropName='checked'>
                  <Controller
                    name='isAssignedAllNewClient'
                    control={control}
                    disabled={isDisable}
                    defaultValue={true}
                    render={({ field }) => (
                      <Checkbox {...field} checked={field.value}>
                        {t('BankSetup:addNewBankUser.label.assignAllNewClientsToThisUser')}
                      </Checkbox>
                    )}
                  />
                </Form.Item>
              </Col>
              <Col span={24} xl={12}>
                <Flex justify='flex-end'>
                  <Button
                    danger
                    type='primary'
                    style={{ height: 42, borderRadius: 12 }}
                    disabled={loading || isDisable}
                    onClick={handleUnassignAllExisting}
                  >
                    {t('ClientUserSetup:unassignAllExisting')}
                  </Button>
                </Flex>
              </Col>
            </Row>
          </Col>
        </Row>
      </Flex>
    </Flex>
  )
}

export default SelectClient
